import React from "react";

import { Box, Button, Typography, useTheme, withStyles } from "@material-ui/core";
import { ExpandLess, ExpandMore, GetApp } from "@material-ui/icons";
import { DataGridPro, GridColDef, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarFilterButton, useGridApiRef } from "@mui/x-data-grid-pro";

import Spacer from "components/Spacer";

import {
    selectClickAndCollectPeakWeekRevenuePrediction,
    selectClickAndCollectTypicalWeekRevenuePrediction,
    selectClickAndCollectTypicalYearRevenuePrediction,
    selectPeakWeekRevenuePrediction,
    selectTypicalWeekRevenuePrediction,
    selectTypicalYearRevenuePrediction,
} from "modules/customer/tools/location/overview/overviewSlice";
import { useAppSelector } from "store";
import numberFormatter from "utils/numberFormatter";

const StyledDataGrid = withStyles(theme => ({
    root: {
        border: "none",
        backgroundColor: theme.palette.background.paper,
        paddingTop: theme.spacing(2),
        paddingLeft: theme.spacing(2),
        "& .MuiCheckbox-root": {
            color: theme.palette.text.primary,
            "&.Mui-checked": {
                //@ts-ignore
                color: theme.palette.tertiary.main,
            },
        },
        "& .MuiDataGrid-row.Mui-selected": {
            backgroundColor: "rgba(255, 255, 255, 0.08)",
            "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.16)",
            },
        },
        "& .MuiDataGrid-cell:focus": {
            outline: "none"
        },
        "& .MuiDataGrid-cell:focus-within": {
            outline: "none"
        }
    }
}))(DataGridPro);

const BreakdownOfRevenue: React.FC = () => {
    const theme = useTheme();
    const typicalYearRevenuePrediction = useAppSelector(selectTypicalYearRevenuePrediction);
    const typicalWeekRevenuePrediction = useAppSelector(selectTypicalWeekRevenuePrediction);
    const peakWeekRevenuePrediction = useAppSelector(selectPeakWeekRevenuePrediction);
    const clickAndCollectTypicalYearRevenuePrediction = useAppSelector(selectClickAndCollectTypicalYearRevenuePrediction);
    const clickAndCollectTypicalWeekRevenuePrediction = useAppSelector(selectClickAndCollectTypicalWeekRevenuePrediction);
    const clickAndCollectPeakWeekRevenuePrediction = useAppSelector(selectClickAndCollectPeakWeekRevenuePrediction);

    const rows = [{
        id: 1,
        revenueType: "In store",
        yearlyRevenue: typicalYearRevenuePrediction,
        weeklyRevenue: typicalWeekRevenuePrediction,
        peakWeekRevenue: peakWeekRevenuePrediction
    }, {
        id: 2,
        revenueType: "Click and collect",
        yearlyRevenue: clickAndCollectTypicalYearRevenuePrediction,
        weeklyRevenue: clickAndCollectTypicalWeekRevenuePrediction,
        peakWeekRevenue: clickAndCollectPeakWeekRevenuePrediction
    }];

    const columns: GridColDef[] = [{
        field: "revenueType",
        flex: 1,
        headerName: "Revenue type",
        sortable: true
    }, {
        field: "yearlyRevenue",
        flex: 1,
        headerName: "Yearly revenue",
        sortable: true,
        renderCell: (params) => numberFormatter.toGBP(params.value ?? 0, 1)
    }, {
        field: "weeklyRevenue",
        flex: 1,
        headerName: "Weekly revenue",
        sortable: true,
        renderCell: (params) => numberFormatter.toGBP(params.value ?? 0, 1)
    }, {
        field: "peakWeekRevenue",
        flex: 1,
        headerName: "Peak week revenue",
        sortable: true,
        renderCell: (params) => numberFormatter.toGBP(params.value ?? 0, 1)
    }];

    const columnsWithWidth = columns.map(column => {
        if (column.type === "singleSelect") {
            //@ts-expect-error
            column.valueOptions = Array.from(new Set(rows.map(row => row[column.field])));
        }
        return {
            ...column,
            minWidth: 150
        };
    });

    const apiRef = useGridApiRef();

    const handleExport = () => {
        apiRef.current.exportDataAsCsv({ fileName: "Dash breakdown of revenue" });
    };

    const CustomExportButton = () => {
        return (
            <Button
                // @ts-ignore
                style={{ color: theme.palette.tertiary.main }}
                startIcon={<GetApp />}
                onClick={handleExport}
                size="small"
            >
                Export
            </Button>
        );
    };

    function CustomToolbar() {
        return (
            <GridToolbarContainer style={{ paddingRight: theme.spacing(2) }}>
                <GridToolbarColumnsButton style={{ color: theme.palette.text.primary }} />
                <GridToolbarFilterButton style={{ color: theme.palette.text.primary }} />
                <Spacer />
                {/* @ts-ignore */}
                <CustomExportButton style={{ color: theme.palette.tertiary.main }} />
            </GridToolbarContainer>
        );
    }

    return (
        <>
            <Box paddingTop={1}>
                <Typography variant="h6">Breakdown of revenue</Typography>
            </Box>
            <br />
            <Box height="100%" width="100%" minHeight="200px">
                <StyledDataGrid
                    rows={rows}
                    columns={columnsWithWidth}
                    checkboxSelection
                    disableSelectionOnClick
                    loading={false}
                    components={{
                        Toolbar: CustomToolbar,
                        ColumnSortedAscendingIcon: ExpandLess,
                        ColumnSortedDescendingIcon: ExpandMore
                    }}
                    sortingOrder={["asc", "desc"]}
                    hideFooter
                    apiRef={apiRef}
                />
            </Box>
            <br />
        </>
    );
};

export default BreakdownOfRevenue;
